import { GraphQLError } from "graphql";
import { User } from "../../Model/User";
import { setSessionCookie, clearSessionCookie } from "./sessionCookie";

const ALLSTAR_USER_ATOM_KEY = "AllstarUserData_v0.1";
const AUTH_PATHS = ["/login", "/register", "/oauth", "/reset"];

const isStaging = process.env.REACT_APP_API_URL?.includes("staging");
const PERSIST_KEY = `${isStaging ? "dev-" : ""}allstar-global-state`;

const AUTH_ERROR_CODES = ["UNAUTHENTICATED"];
const AUTH_MESSAGE_PATTERN =
  /must be logged in|logged in to query|not authenticated|unauthori[sz]ed|jwt (?:expired|malformed)|invalid token|token expired/i;

export const isClientSideLoggedIn = (): boolean => {
  if (localStorage.getItem("token")) return true;
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.[ALLSTAR_USER_ATOM_KEY]?.loggedIn === true;
  } catch {
    return false;
  }
};

export const isAuthError = (
  graphQLErrors: readonly GraphQLError[] | undefined,
  networkError: unknown,
): boolean => {
  const statusCode = (networkError as { statusCode?: number } | null)
    ?.statusCode;
  if (statusCode === 401) return true;

  return !!graphQLErrors?.some((error) => {
    const code = error.extensions?.code as string | undefined;
    if (code && AUTH_ERROR_CODES.includes(code)) return true;
    return AUTH_MESSAGE_PATTERN.test(error.message ?? "");
  });
};

let inFlightRefresh: Promise<string | null> | null = null;

const refresh = async (): Promise<string | null> => {
  try {
    const userModel = User.getInstance();
    const decoded = await userModel.DecodeToken();
    const refreshToken = decoded?.refreshToken;
    if (!refreshToken) return null;

    const newToken = await userModel.RefreshTokenOrThrow(refreshToken);
    localStorage.setItem("token", newToken);
    setSessionCookie(newToken);
    return newToken;
  } catch {
    return null;
  }
};

export const tryRefreshToken = (): Promise<string | null> => {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = refresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
};

const clearPersistedUser = (): void => {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    delete parsed[ALLSTAR_USER_ATOM_KEY];
    localStorage.setItem(PERSIST_KEY, JSON.stringify(parsed));
  } catch {
    localStorage.removeItem(PERSIST_KEY);
  }
};

let loggingOut = false;

export const forceLogout = (logoutReason: string): void => {
  if (loggingOut) return;
  loggingOut = true;

  localStorage.removeItem("token");
  localStorage.removeItem("payload");
  clearSessionCookie();
  clearPersistedUser();

  window.rudderanalytics?.track?.("Logout", { logoutReason });

  const { pathname } = window.location;
  const onAuthPage = AUTH_PATHS.some((path) => pathname.startsWith(path));
  window.location.assign(
    onAuthPage ? "/login" : `/login?next=${encodeURIComponent(pathname)}`,
  );
};

export function generateCodeVerifier(length: number = 128): string {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (dec) => ("0" + dec.toString(16)).substr(-2)).join(
    "",
  );
}

export async function generateCodeChallenge(
  codeVerifier: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return btoa(
    String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))),
  )
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// Used for battle net oauth state param
export function generateBNetOAuthState() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64.replace(/[+/=]/g, "");
}
